import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Plus, Search, MoreVertical, Eye, Copy, Send, Ban, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from "react-toastify";
import useApi from "@src/api_service/useApi";
import LoadingStateComp from "@src/components/LoadingComponent/LoadingComponent";
import Pagination from "@src/components/Pagination/Pagination";
import { cn } from "@src/utils/app_functions";
import { ApiErrorBody, LinkType, SchoolPaymentLink, SchoolPaymentLinkStatus, StatusSummary } from "./interface";
import {
  LINK_STATUSES,
  LINK_TYPE_LABELS,
  StatusPill,
  authHeaders,
  copyToClipboard,
  errorMessage,
  formatDate,
  formatMoney,
  isOnboardingLink,
} from "./utils";
import { CancelLinkModal, useLinkActions } from "./components/LinkActions";

type LayoutContextType = {
  setSchoolAdminPageName: React.Dispatch<React.SetStateAction<string>>;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
  setMenu: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>;
  setIsAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
};

interface Filters {
  status: SchoolPaymentLinkStatus | '';
  linkType: LinkType | '';
  appId: string;
  email: string;
}

const EMPTY_SUMMARY: StatusSummary = { active: 0, paid: 0, expired: 0, cancelled: 0 };

const SUMMARY_ACCENT: Record<SchoolPaymentLinkStatus, string> = {
  active: 'text-green-700',
  paid: 'text-emerald-700',
  expired: 'text-gray-600',
  cancelled: 'text-red-600',
};

const SchoolPaymentLinksPage: React.FC = () => {
  const navigate = useNavigate();
  const path = window.location.pathname;
  const { setSchoolAdminPageName, setShowInput, setMenu, setIsAffiliate } = useOutletContext<LayoutContextType>();

  const [links, setLinks] = useState<SchoolPaymentLink[]>([]);
  const [summary, setSummary] = useState<StatusSummary>(EMPTY_SUMMARY);
  const [filters, setFilters] = useState<Filters>({ status: '', linkType: '', appId: '', email: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<SchoolPaymentLink | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { loading, get } = useApi();

  useEffect(() => {
    setSchoolAdminPageName("School Payment Links");
    setShowInput(false);
    setMenu(undefined);
    setIsAffiliate(false);
  }, [path]);

  const fetchLinks = useCallback(async (activeFilters: Filters, page: number, showLoading = true) => {
    const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
    if (activeFilters.status) params.set('status', activeFilters.status);
    if (activeFilters.linkType) params.set('linkType', activeFilters.linkType);
    if (activeFilters.appId.trim()) params.set('appId', activeFilters.appId.trim().toUpperCase());
    if (activeFilters.email.trim()) params.set('email', activeFilters.email.trim());

    await get<{
      data: { links: SchoolPaymentLink[]; summary: StatusSummary };
      pagination?: { total: number; page: number; totalPages: number };
    }, ApiErrorBody>(
      `/third_party/school_payment_links?${params.toString()}`,
      authHeaders(false),
      {
        loading: showLoading,
        onSuccess: (response) => {
          setLinks(response.data?.links ?? []);
          setSummary({ ...EMPTY_SUMMARY, ...(response.data?.summary ?? {}) });
          if (response.pagination) {
            setTotalPages(Math.max(response.pagination.totalPages, 1));
            setCurrentPage(response.pagination.page);
            setTotal(response.pagination.total);
          }
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Could not load payment links'));
          setLinks([]);
        },
      }
    );
  }, [get, pageSize]);

  useEffect(() => {
    fetchLinks(filters, 1);
  }, []);

  const refresh = () => fetchLinks(filters, currentPage, false);
  const actions = useLinkActions(refresh);

  const applyFilters = (next: Filters) => {
    setFilters(next);
    setCurrentPage(1);
    fetchLinks(next, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchLinks(filters, page);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopy = async (link: SchoolPaymentLink) => {
    setOpenDropdownId(null);
    if (await copyToClipboard(link.url)) toast.success('Payment link copied');
    else toast.error('Could not copy the link');
  };

  const handleResend = (link: SchoolPaymentLink) => {
    setOpenDropdownId(null);
    actions.resend(link);
  };

  const handleCancelConfirm = async (reason: string) => {
    if (!cancelTarget) return;
    await actions.cancel(cancelTarget, reason);
    setCancelTarget(null);
  };

  const hasFilters = Boolean(filters.status || filters.linkType || filters.appId || filters.email);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">School Payment Links</h1>
            <p className="text-gray-600 mt-1">Send schools a link to pay for a plan or their onboarding fee at a negotiated price</p>
          </div>
          <button
            onClick={() => navigate('/home/school-payment-links/create')}
            className="flex items-center gap-2 px-6 py-3 bg-[#E55109] text-white rounded-lg hover:bg-[#d14808] transition-colors shadow-md hover:shadow-lg"
          >
            <Plus className="w-5 h-5" />
            Create Payment Link
          </button>
        </div>

        {/* Status summary: counts ignore the status filter, so each card doubles as a filter */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {LINK_STATUSES.map((status) => (
            <button
              key={status}
              onClick={() => applyFilters({ ...filters, status: filters.status === status ? '' : status })}
              className={cn(
                "text-left bg-white rounded-lg shadow-sm border-2 p-4 transition-colors",
                filters.status === status ? "border-[#E55109]" : "border-transparent hover:border-gray-200"
              )}
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 capitalize">{status}</div>
              <div className={cn("text-2xl font-bold mt-1", SUMMARY_ACCENT[status])}>{summary[status]}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters(filters);
          }}
          className="flex flex-wrap gap-3 mb-6"
        >
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="App ID (THD-XXXXXXXXXX)"
              value={filters.appId}
              onChange={(e) => setFilters((prev) => ({ ...prev, appId: e.target.value }))}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none"
            />
          </div>
          <input
            type="email"
            placeholder="Recipient email"
            value={filters.email}
            onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
            className="flex-1 min-w-[200px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none"
          />
          <select
            value={filters.status}
            onChange={(e) => applyFilters({ ...filters, status: e.target.value as Filters['status'] })}
            className="px-4 py-3 border border-gray-300 rounded-lg bg-white capitalize focus:ring-2 focus:ring-[#E55109] outline-none"
          >
            <option value="">All statuses</option>
            {LINK_STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={filters.linkType}
            onChange={(e) => applyFilters({ ...filters, linkType: e.target.value as Filters['linkType'] })}
            className="px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#E55109] outline-none"
          >
            <option value="">All types</option>
            {(Object.keys(LINK_TYPE_LABELS) as LinkType[]).map((type) => (
              <option key={type} value={type}>{LINK_TYPE_LABELS[type]}</option>
            ))}
          </select>
          <button type="submit" className="px-5 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium">
            Search
          </button>
          {hasFilters && (
            <button
              type="button"
              onClick={() => applyFilters({ status: '', linkType: '', appId: '', email: '' })}
              className="px-4 py-3 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            title="Refresh"
            onClick={() => fetchLinks(filters, currentPage)}
            className="px-3 py-3 border border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </form>

        {loading ? (
          <LoadingStateComp Message="Payment Links" />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {links.length === 0 ? (
                <div className="text-center py-16">
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No payment links found</h3>
                  <p className="text-gray-500">
                    {hasFilters ? 'Try adjusting your filters' : 'Create a link once a price has been agreed with a school'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">School</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Pays for</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Expires / Paid</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {links.map((link) => (
                        <tr
                          key={link._id}
                          onClick={() => navigate(`/home/school-payment-links/${link._id}`)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <td className="px-6 py-4">
                            <div className="font-semibold text-gray-900">{link.organizationName || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{link.thirdPartyAppId}</div>
                            <div className="text-sm text-gray-500">{link.recipientEmail}</div>
                          </td>
                          <td className="px-6 py-4">
                            {isOnboardingLink(link) ? (
                              <>
                                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  Onboarding fee
                                </span>
                                <div className="text-sm text-gray-500 mt-1">
                                  {(link.numberOfStudents ?? 0).toLocaleString()} student slots · one-time
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-sm text-gray-900 flex items-center gap-1.5">
                                  {link.planName}
                                  {link.outsidePlanRange && (
                                    <span title="Student count is outside this plan's range">
                                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 capitalize">
                                  {(link.numberOfStudents ?? 0).toLocaleString()} students · {link.billingCycle}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap">
                            {formatMoney(link.amount, link.currency)}
                          </td>
                          <td className="px-6 py-4">
                            <StatusPill status={link.status} />
                            {!link.emailSentAt && link.status === 'active' && (
                              <div className="text-xs text-red-600 mt-1">Email not sent</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                            {link.status === 'paid' ? formatDate(link.paidAt) : formatDate(link.expiresAt)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {formatDate(link.createdAt)}
                            {typeof link.createdBy === 'object' && link.createdBy?.fullname && (
                              <div className="text-xs">by {link.createdBy.fullname}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="relative inline-block" ref={openDropdownId === link._id ? dropdownRef : null}>
                              <button
                                onClick={() => setOpenDropdownId(openDropdownId === link._id ? null : link._id)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>
                              {openDropdownId === link._id && (
                                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 text-left">
                                  <button
                                    onClick={() => navigate(`/home/school-payment-links/${link._id}`)}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <Eye className="w-4 h-4" /> View details
                                  </button>
                                  <button
                                    onClick={() => handleCopy(link)}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <Copy className="w-4 h-4" /> Copy link
                                  </button>
                                  {link.status === 'active' && (
                                    <>
                                      <button
                                        onClick={() => handleResend(link)}
                                        disabled={actions.loading}
                                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      >
                                        <Send className="w-4 h-4" /> Resend email
                                      </button>
                                      <button
                                        onClick={() => {
                                          setOpenDropdownId(null);
                                          setCancelTarget(link);
                                        }}
                                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                      >
                                        <Ban className="w-4 h-4" /> Cancel link
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {links.length > 0 && (
              <div className="mt-6">
                <div className="text-sm text-gray-500 text-center">{total} link{total === 1 ? '' : 's'}</div>
                <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
              </div>
            )}
          </>
        )}
      </div>

      <CancelLinkModal
        link={cancelTarget}
        loading={actions.loading}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
      />
    </div>
  );
};

export default SchoolPaymentLinksPage;
